/**
 * useAccountFeatureWith — whether one optional module is switched on for an
 * account. One cached request per account (every key reads the same list),
 * shared by every caller through React Query, so a tab, a step and a chip
 * asking about the same account cost one GET between them.
 *
 * Off until known: while loading, on a failed read, and with no account, the
 * answer is `false` — an optional module must never appear by mistake, and a
 * failed read must never break the screen around it.
 *
 * Web and mobile wrap this with their own client (web: useAccountFeature).
 */
import { useContext } from 'react';
import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query';
import { getAccountFeatures } from '../services/accountFeatures';
export const accountFeaturesQueryKey = (accountId) => ['account-features', accountId];
/** Fresh for a minute: switching a feature is rare, and the settings switch refreshes the entry itself. */
const STALE_MS = 60000;
/**
 * Used only where no QueryClientProvider is mounted (a screen rendered on its
 * own, e.g. in a test): an optional module's check must never be what breaks
 * a core screen.
 */
let fallbackClient = null;
const fallback = () => (fallbackClient ?? (fallbackClient = new QueryClient({ defaultOptions: { queries: { retry: false } } })));
export function useAccountFeatureWith(client, accountId, key, options = {}) {
    const enabled = (options.enabled ?? true) && !!accountId;
    const provided = useContext(QueryClientContext);
    const q = useQuery({
        queryKey: accountFeaturesQueryKey(accountId ?? ''),
        queryFn: () => getAccountFeatures(client, accountId),
        enabled,
        staleTime: STALE_MS,
        retry: false,
    }, provided ?? fallback());
    return { on: enabled && !!q.data?.includes(key), loaded: !enabled || q.isFetched || q.isError };
}
